import React, { useRef, useCallback } from 'react';
import { Box } from '@mui/material';
import { tokens } from '../../theme';
import { useSmartCompose } from '../../hooks/useSmartCompose';

/**
 * SmartComposeEditor — textarea with ghost-text autocomplete.
 * Tab: accept suggestion. Esc: dismiss. Any other key while ghost shown: clear.
 */
function SmartComposeEditor({
  value = '',
  onChange,
  threadId = '',
  clientId = '',
  placeholder = 'Type your message…',
  sx = {},
  rows = 5,
  ...rest
}) {
  const textareaRef = useRef(null);
  const { suggestion } = useSmartCompose(value, threadId, clientId);

  const handleKeyDown = useCallback(
    (e) => {
      if (!suggestion) return;
      if (e.key === 'Tab') {
        e.preventDefault();
        onChange(value + suggestion);
      } else if (e.key === 'Escape') {
        // Suggestion cleared automatically by value change (no-op needed here,
        // but we stop propagation so the dialog doesn't close on Esc)
        e.stopPropagation();
      }
      // Any other key: suggestion becomes stale; the next useSmartCompose
      // invocation on the next render will reset it via the useEffect.
    },
    [suggestion, value, onChange]
  );

  const lineHeight = 1.5;
  const fontSize = 15;
  const paddingPx = 12;
  const sharedStyle = {
    width: '100%',
    boxSizing: 'border-box',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    fontSize,
    lineHeight,
    padding: `${paddingPx}px`,
    border: 'none',
    outline: 'none',
    resize: 'vertical',
    minHeight: `${rows * fontSize * lineHeight + paddingPx * 2}px`,
    background: 'transparent',
    display: 'block',
  };

  return (
    <Box
      sx={{
        position: 'relative',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        overflow: 'hidden',
        '&:focus-within': { borderColor: 'primary.main', boxShadow: '0 0 0 2px rgba(62,76,232,0.15)' },
        ...sx,
      }}
    >
      {/* Ghost text layer — rendered behind the real textarea */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          pointerEvents: 'none',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          color: 'transparent',
          zIndex: 0,
          ...sharedStyle,
        }}
      >
        {value}
        <Box
          component="span"
          sx={{ color: tokens.color.ink3 }}
        >
          {suggestion}
        </Box>
      </Box>

      {/* Real textarea — transparent background so ghost shows through */}
      <Box
        ref={textareaRef}
        component="textarea"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={!value ? placeholder : ''}
        spellCheck
        sx={{
          ...sharedStyle,
          position: 'relative',
          zIndex: 1,
          color: 'text.primary',
          caretColor: 'text.primary',
          '&::placeholder': { color: tokens.color.ink3 },
        }}
        {...rest}
      />
    </Box>
  );
}

export default SmartComposeEditor;
